/**
 * Help links used across both portals: opening the help panel, the "How this works" link in screen headers and
 * the one-line hint in empty states. Kept apart from Help.tsx so the shared components can use them.
 */
import { Icon } from "../components/Icon";
import { useHash } from "../lib/route";
import { currentRoute, helpPage, pageForRoute } from "./data";

export type PanelState = { kind: "page"; id: string } | { kind: "report" } | null;
export const listeners = new Set<(s: PanelState) => void>();
const show = (s: PanelState) => listeners.forEach((l) => l(s));
export const openHelp = (id: string) => show({ kind: "page", id });
export const openReport = () => show({ kind: "report" });

/**
 * "How this works" for the current screen (or a given page), when the client layer has a guide for it. `compact`:
 * a smaller underlined link, for a section inside a screen.
 */
export function HowThisWorks({ route, pageId, label = "How this works", compact }: { route?: string; pageId?: string; label?: string; compact?: boolean }) {
  useHash();
  const page = pageId ? helpPage(pageId) : pageForRoute(route ?? currentRoute());
  if (!page) return null;
  return (
    <button type="button" className={compact ? "helplink--compact" : "helplink"} onClick={() => openHelp(page.id)} aria-haspopup="dialog">
      <Icon name="help" size={compact ? 16 : 18} /> {label}
    </button>
  );
}

/** A small "How this works" link for one section of a screen (for example the checklists in a job pack). */
export function SectionHelp({ pageId, children }: { pageId: string; children?: string }) {
  if (!helpPage(pageId)) return null;
  return <HowThisWorks pageId={pageId} compact label={children ?? "How this works"} />;
}

/** One line from the screen's guide under an empty list, with a link to the guide. */
export function HelpHint({ route, className = "helphint" }: { route?: string; className?: string }) {
  useHash();
  const page = pageForRoute(route ?? currentRoute());
  if (!page) return null;
  return (
    <p className={className}>
      {page.summary}{" "}
      <button type="button" className="linkbtn" onClick={() => openHelp(page.id)}>Read the guide</button>
    </p>
  );
}
